import { PropertiesPanelContent, PropertiesPanelPropertyConfig } from '../../../../../widgets/propertiesPanel';
import { T } from 'src/shared/helpers';

export type LightConfigHelpers = {
    updateProperty: <K extends keyof LightNode>(key: K, value: LightNode[K]) => void;
    updateVector: (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => void;
    handleColorChange: (key: 'diffuseColor' | 'groundColor') => (_: any, color: string) => void;
    /** Riporta la luce al centro e a guardare in giù. */
    resetTransform: () => void;
    /** Cambia la specie: dietro, una luce muore e una nasce. */
    changeType: (lightType: string) => void;
};

// SOTTO IL NOME CI VA LA PAROLA, non il nome del dato. «point_light» è come il motore 3D chiama
// quel tipo di luce; chi guarda legge «Point light». Sta qui e non nel pannello perché la
// leggono in due — la testata, che la scrive sotto il nome, e la card del tipo.
export const LIGHT_KIND: Record<string, string> = {
    point_light: T._('Point light', 'Admin'),
    directional_light: T._('Directional light', 'Admin'),
    spot_light: T._('Spot light', 'Admin'),
    hemispheric_light: T._('Hemispheric light', 'Admin')
};

// Le quattro specie come le chiede un menu. Derivate dalla tavola qui sopra e non riscritte: due
// elenchi degli stessi quattro nomi sono uno che un giorno resta indietro.
export const LIGHT_KIND_OPTIONS = Object.entries(LIGHT_KIND).map(([value, label]) => ({ value, label }));

// LE STESSE QUATTRO SPECIE IN UNA PAROLA, per il codino di una riga d'elenco. Qui la tavola
// sopra non basta e non si può ricavarla togliendo «light» dalla sua: quella parola in un'altra
// lingua non sta in fondo, e in certe non c'è affatto. Sono quattro etichette, e come tali si
// traducono.
export const LIGHT_KIND_SHORT: Record<string, string> = {
    point_light: T._('Point', 'Admin'),
    directional_light: T._('Directional', 'Admin'),
    spot_light: T._('Spot', 'Admin'),
    hemispheric_light: T._('Hemispheric', 'Admin')
};

export type LightNode = {
    id: string;
    name: string;
    lightType: string;
    /** Accesa o spenta nella scena. È lo stesso dato che l'occhio della lista commuta. */
    active?: boolean;
    intensity: number;
    diffuseColor: string;
    groundColor?: string;
    position?: [number, number, number];
    direction?: [number, number, number];
    angle?: number;
    exponent?: number;
};

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Configurazione per un input triplo (x, y, z) per vettori
const createVectorConfig = (
    id: string,
    label: string,
    values: [number, number, number],
    onChange: (axis: 'x' | 'y' | 'z', value: number) => void
): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'point',
        id,
        label,
        coords: [
            {
                id: 'x',
                label: 'X',
                step: 0.01,
                value: values[0],
                onChange: (newValue) => onChange('x', newValue)
            },
            {
                id: 'y',
                label: 'Y',
                step: 0.01,
                value: values[1],
                onChange: (newValue) => onChange('y', newValue)
            },
            {
                id: 'z',
                label: 'Z',
                step: 0.01,
                value: values[2],
                onChange: (newValue) => onChange('z', newValue)
            }
        ]
    });
};

// LA COLONNA HA DUE CAPITOLI, e le card dentro portano i nomi del mockup.
//
// Prima erano due gruppi piatti — «Basic Properties» e «Light Type Properties» — e il secondo
// nome non dice niente a chi guarda: raccoglieva la posizione di una luce puntiforme insieme al
// colore del terreno di una emisferica, cioè cose di natura diversa messe insieme dall'unica
// proprietà che avevano in comune, essere condizionali.
//
// Ora il criterio è quello del mockup: DOVE sta la luce (SCENE › Transform) e COM'È fatta la sua
// luce (SHADING › Light). Le proprietà condizionali cadono nel capitolo che le riguarda, e una
// card vuota non si mostra: una luce emisferica non ha posizione, e un contenitore che si apre
// sul niente dice che manca qualcosa.

const transformProperties = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const properties: PropertiesPanelPropertyConfig[] = [];

    // La posizione ce l'hanno la puntiforme e il faretto: una direzionale illumina da un verso, e
    // una emisferica arriva da tutto il cielo.
    if (light.lightType === 'point_light' || light.lightType === 'spot_light') {
        properties.push(
            createVectorConfig('position', T._('Position', 'Admin'), light.position || [0, 0, 0], (axis, value) =>
                helpers.updateVector('position', axis, value)
            )
        );
    }

    // ANCHE UN CIELO HA UN VERSO, e mancava. Per una emisferica la direzione è dove sta l'ALTO: da
    // lì scende il colore del cielo e dalla parte opposta sale quello del suolo, e inclinarla
    // ribalta il gradiente. Non ha una posizione — un cielo non sta da nessuna parte — ma dire dov'è
    // il suo alto è la metà del suo mestiere, ed era l'unica specie senza card Transform.
    const aims = light.lightType === 'directional_light' || light.lightType === 'spot_light' || light.lightType === 'hemispheric_light';

    if (aims) {
        const fallback: [number, number, number] = light.lightType === 'hemispheric_light' ? [0, 1, 0] : [0, -1, 0];

        properties.push(
            createVectorConfig('direction', T._('Direction', 'Admin'), light.direction || fallback, (axis, value) =>
                helpers.updateVector('direction', axis, value)
            )
        );
    }

    // AZZERARE STA SOTTO I NUMERI e non sopra, come nel pannello di un oggetto: la si sceglie
    // dopo aver visto che cosa si sta per buttare via. Per una luce «azzerata» vuol dire al
    // centro della scena e rivolta in giù — non c'è una scala né una rotazione da riportare a
    // uno. E non si scrive se non c'è niente da azzerare: una emisferica è un cielo, non sta da
    // nessuna parte.
    if (properties.length) {
        properties.push(
            createPropertyConfig({
                type: 'action',
                id: 'light-reset-transform',
                label: T._('Reset transform', 'Admin'),
                onClick: helpers.resetTransform
            })
        );
    }

    return properties;
};

const lightProperties = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const properties: PropertiesPanelPropertyConfig[] = [
        // IL NOME NON STA QUI: sta nella testata dello Inspector, dove si legge e si modifica. Due
        // campi per la stessa cosa, uno sopra lo altro, sembrano due nomi.
        createPropertyConfig({
            type: 'range',
            id: 'intensity',
            label: T._('Intensity', 'Admin'),
            value: typeof light.intensity === 'number' ? light.intensity : 1,
            min: 0.1,
            max: 10,
            step: 0.1,
            onChange: (value) => helpers.updateProperty('intensity', value)
        }),
        createPropertyConfig({
            type: 'color',
            id: 'diffuseColor',
            label: T._('Diffuse Color', 'Admin'),
            value: normalizeHexColor(light.diffuseColor),
            onChange: (value) => helpers.handleColorChange('diffuseColor')(null, value)
        })
    ];

    // SOPRA QUELLO CHE HA QUALUNQUE LUCE, SOTTO QUELLO CHE PORTA IL TIPO, e in mezzo una linea.
    // Non è decorazione: la parte di sotto è quella che cambia cambiando specie — il suolo di una
    // emisferica, il cono di un faretto — e la linea è ciò che lo lascia prevedere. Se sotto non
    // c'è niente la linea non si scrive: una direzionale ha solo intensità e colori, e un divisore
    // che non separa niente promette una seconda metà che non arriva.
    const hasOwnParameters = light.lightType === 'hemispheric_light' || light.lightType === 'spot_light';

    if (hasOwnParameters) {
        properties.push(createPropertyConfig({ type: 'divider', id: 'light-own-parameters' }));
    }

    // Il colore del terreno è la seconda metà di una emisferica: sopra il cielo, sotto il suolo.
    if (light.lightType === 'hemispheric_light') {
        properties.push(
            createPropertyConfig({
                type: 'color',
                id: 'groundColor',
                label: T._('Ground Color', 'Admin'),
                value: normalizeHexColor(light.groundColor || '#000000'),
                onChange: (value) => helpers.handleColorChange('groundColor')(null, value)
            })
        );
    }

    // La forma del cono è del solo faretto.
    if (light.lightType === 'spot_light') {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'angle',
                label: T._('Angle', 'Admin'),
                value: typeof light.angle === 'number' ? light.angle : 30,
                min: 0,
                max: 180,
                step: 0.1,
                unit: '°',
                onChange: (value) => helpers.updateProperty('angle', value)
            }),
            createPropertyConfig({
                type: 'range',
                id: 'exponent',
                label: T._('Falloff', 'Admin'),
                value: typeof light.exponent === 'number' ? light.exponent : 1,
                min: 1,
                max: 128,
                step: 1,
                onChange: (value) => helpers.updateProperty('exponent', value)
            })
        );
    }

    return properties;
};

// IL PANNELLO DI UNA LUCE HA DUE CAPITOLI, non tre: quello che la luce È e dove sta — il tipo, la
// trasformazione, la luce che emette — e quello che riguarda il suo comportamento nella scena.
// «Shading» era il capitolo dei materiali prestato alle luci: una luce non ombreggia niente, la
// luce È.
const lightPropertiesByType = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelContent[] => {
    const sections: PropertiesPanelContent[] = [];
    const transform = transformProperties(light, helpers);

    const scene: PropertiesPanelPropertyConfig[] = [
        // IL TIPO SI CAMBIA, E CAMBIARLO RIFÀ LA LUCE. In Babylon la specie non è un attributo: una
        // puntiforme e un faretto sono due classi diverse, e non esiste un metodo che trasformi
        // l'una nell'altra. Dietro questo menu c'è dunque una luce che muore e una che nasce, col
        // nome, i colori, il posto e lo stato della prima.
        //
        // SI PERDE SOLO CIÒ CHE IL TIPO NUOVO NON SA PORTARE — il cono di un faretto su una
        // puntiforme non vuol dire niente. Ed è la scelta da cui dipende metà del pannello: quali
        // card ci sono e quali no. Per questo sta in cima, e si legge prima del resto.
        //
        // La card non si apre perché quello che dice sta tutto nella sua testata.
        createPropertyConfig({
            type: 'group',
            id: 'light-type',
            header: T._('Type', 'Admin'),
            fixed: true,
            trailing: createPropertyConfig({
                type: 'select',
                id: 'light-kind',
                label: T._('Light type', 'Admin'),
                options: LIGHT_KIND_OPTIONS,
                value: { value: light.lightType, label: LIGHT_KIND[light.lightType] ?? light.lightType },
                onChange: (option) => {
                    if (option?.value && option.value !== light.lightType) helpers.changeType(String(option.value));
                }
            }),
            properties: []
        })
    ];

    if (transform.length) {
        scene.push(
            createPropertyConfig({
                type: 'group',
                id: 'light-transform',
                header: T._('Transform', 'Admin'),
                defaultIsOpen: true,
                properties: transform
            })
        );
    }

    scene.push(
        createPropertyConfig({
            type: 'group',
            id: 'light-light',
            header: T._('Light', 'Admin'),
            defaultIsOpen: true,
            properties: lightProperties(light, helpers)
        })
    );

    sections.push(
        createPropertyConfig({
            type: 'section',
            id: 'light-scene',
            title: T._('Scene', 'Admin'),
            properties: scene
        })
    );

    sections.push(
        createPropertyConfig({
            type: 'section',
            id: 'light-settings',
            title: T._('Settings', 'Admin'),
            properties: [
                createPropertyConfig({
                    type: 'group',
                    id: 'light-rendering',
                    header: T._('Rendering', 'Admin'),
                    defaultIsOpen: true,
                    properties: [
                        // ACCESA È LO STESSO DATO DELL'OCCHIO NELLA LISTA, e deve restare uno: due
                        // comandi che scrivono la stessa cosa in due posti si scollano al primo che
                        // qualcuno tocca senza guardare l'altro.
                        createPropertyConfig({
                            type: 'switch',
                            id: 'light-active',
                            label: T._('Active', 'Admin'),
                            checked: light.active !== false,
                            onChange: (checked: boolean) => helpers.updateProperty('active', checked)
                        })
                    ]
                })
            ]
        })
    );

    return sections;
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useLightPropertiesCreator = (): ((light: LightNode, helpers: LightConfigHelpers) => PropertiesPanelContent[]) => {
    return (light: LightNode, helpers: LightConfigHelpers) => {
        return lightPropertiesByType(light, helpers);
    };
};
